import React from 'react';
import { Box, Button, Chip, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import InboxRoundedIcon from '@mui/icons-material/InboxRounded';

export const analyticsMetricCardSx = (accent) => ({
    px: { xs: 2.75, sm: 3 },
    py: { xs: 1.75, sm: 2 },
    minHeight: { xs: 132, sm: 148 },
    borderRadius: 4,
    position: 'relative',
    overflow: 'hidden',
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.92)} 0%, ${alpha('#f7fbff', 0.78)} 100%)`,
    border: `1px solid ${alpha('#ffffff', 0.72)}`,
    boxShadow: '0 16px 32px rgba(15, 23, 42, 0.08)',
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: { xs: 22, sm: 24 },
        right: { xs: 22, sm: 24 },
        height: 4,
        borderRadius: '0 0 6px 6px',
        background: accent
    }
});

export const analyticsPanelSx = {
    borderRadius: 5,
    overflow: 'hidden',
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.9)} 0%, ${alpha('#f8fbff', 0.78)} 100%)`,
    boxShadow: '0 24px 54px rgba(15, 23, 42, 0.10)',
    border: `1px solid ${alpha('#ffffff', 0.72)}`,
    backdropFilter: 'blur(18px)',
    WebkitBackdropFilter: 'blur(18px)'
};

export const analyticsPanelHeaderSx = {
    px: { xs: 2, sm: 2.75 },
    pt: { xs: 2, sm: 2.4 },
    pb: { xs: 1.4, sm: 1.5 },
    borderBottom: `1px solid ${alpha('#0f172a', 0.06)}`,
    background: `linear-gradient(180deg, ${alpha('#ffffff', 0.52)} 0%, ${alpha('#f4f8fc', 0.22)} 100%)`
};

export const analyticsPanelBodySx = {
    px: { xs: 2, sm: 2.75 },
    py: { xs: 2, sm: 2.5 }
};

export const analyticsTableSx = {
    minWidth: 720,
    '& .MuiTableCell-root': {
        borderColor: alpha('#0f172a', 0.06)
    }
};

export const analyticsHeadCellSx = {
    py: 1.35,
    px: 1.6,
    backgroundColor: alpha('#f8fbff', 0.78),
    color: 'text.secondary',
    fontWeight: 800,
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    fontSize: '0.69rem',
    whiteSpace: 'nowrap'
};

export const analyticsBodyCellSx = {
    px: 1.6,
    py: 1.45,
    fontSize: '0.92rem',
    verticalAlign: 'top'
};

export const glassSubCardSx = {
    p: 1.4,
    borderRadius: 3,
    backgroundColor: alpha('#ffffff', 0.58),
    border: `1px solid ${alpha('#0f172a', 0.05)}`
};

export function AnalyticsPageHero({ title, description, chips = [], actions = null, sx }) {
    return (
        <Paper
            sx={[
                {
                    ...analyticsPanelSx,
                    position: 'relative',
                    overflow: 'hidden',
                    background: `linear-gradient(135deg, ${alpha('#ffffff', 0.94)} 0%, ${alpha('#f4f8fc', 0.88)} 44%, ${alpha('#eef5fb', 0.76)} 100%)`,
                    '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: 0,
                        background: `radial-gradient(circle at top right, ${alpha('#60a5fa', 0.16)}, transparent 36%), radial-gradient(circle at bottom left, ${alpha('#0f766e', 0.1)}, transparent 32%)`,
                        pointerEvents: 'none'
                    }
                },
                sx
            ]}
        >
            <Stack
                direction={{ xs: 'column', lg: 'row' }}
                justifyContent="space-between"
                spacing={2}
                sx={{ position: 'relative', zIndex: 1, px: { xs: 2, sm: 2.75 }, py: { xs: 2.4, sm: 3 } }}
            >
                <Stack spacing={1.2} sx={{ maxWidth: 820 }}>
                    <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: '-0.05em', maxWidth: 640 }}>
                        {title}
                    </Typography>
                    {description ? (
                        <Typography variant="body1" sx={{ color: 'text.secondary', maxWidth: 760 }}>
                            {description}
                        </Typography>
                    ) : null}
                    {chips.length ? (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} flexWrap="wrap">
                            {chips.map((chip) => (
                                <Chip
                                    key={chip}
                                    label={chip}
                                    variant="outlined"
                                    sx={{ fontWeight: 700, backgroundColor: alpha('#ffffff', 0.52) }}
                                />
                            ))}
                        </Stack>
                    ) : null}
                </Stack>
                {actions ? (
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} alignItems={{ sm: 'flex-start' }}>
                        {actions}
                    </Stack>
                ) : null}
            </Stack>
        </Paper>
    );
}

export function AnalyticsMetricCard({ icon, label, value, caption, accent }) {
    return (
        <Paper sx={analyticsMetricCardSx(accent)}>
            <Stack spacing={1.2} sx={{ position: 'relative', zIndex: 1 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.25}>
                    <Typography sx={{ color: 'text.secondary', fontSize: '0.72rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        {label}
                    </Typography>
                    <Box sx={{ width: 36, height: 36, borderRadius: '50%', display: 'grid', placeItems: 'center', color: 'text.primary', backgroundColor: alpha('#ffffff', 0.58), border: `1px solid ${alpha('#ffffff', 0.52)}` }}>
                        {icon}
                    </Box>
                </Stack>
                <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.6rem', sm: '1.9rem' }, lineHeight: 1, letterSpacing: '-0.04em' }}>
                    {value}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', maxWidth: 260 }}>
                    {caption}
                </Typography>
            </Stack>
        </Paper>
    );
}

export function AnalyticsPanelSection({ eyebrow, title, subtitle, right, children, bodySx, sx, headerSx }) {
    return (
        <Paper sx={[analyticsPanelSx, sx]}>
            <Box sx={[analyticsPanelHeaderSx, headerSx]}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} justifyContent="space-between" alignItems={{ sm: 'center' }}>
                    <Box>
                        {eyebrow ? (
                            <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 800, letterSpacing: '0.08em' }}>
                                {eyebrow}
                            </Typography>
                        ) : null}
                        <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
                            {title}
                        </Typography>
                        {subtitle ? (
                            <Typography variant="body2" sx={{ mt: 0.35, color: 'text.secondary', maxWidth: 760 }}>
                                {subtitle}
                            </Typography>
                        ) : null}
                    </Box>
                    {right ? <Box sx={{ flexShrink: 0 }}>{right}</Box> : null}
                </Stack>
            </Box>
            <Box sx={{ ...analyticsPanelBodySx, ...(bodySx || {}) }}>{children}</Box>
        </Paper>
    );
}

export function AnalyticsInlineState({
    kind = 'empty',
    title,
    message,
    actionLabel,
    onAction,
    compact = false
}) {
    const mode = String(kind || 'empty').trim().toLowerCase();

    if (mode === 'loading') {
        return (
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: compact ? 0.5 : 1 }}>
                <CircularProgress size={compact ? 16 : 18} />
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {message || 'Loading...'}
                </Typography>
            </Stack>
        );
    }

    const icon = mode === 'error'
        ? <ErrorOutlineRoundedIcon fontSize="small" />
        : <InboxRoundedIcon fontSize="small" />;
    const accent = mode === 'error' ? '#b91c1c' : '#1d4ed8';
    const tint = mode === 'error'
        ? alpha('#fef2f2', 0.95)
        : alpha('#eff6ff', 0.88);

    return (
        <Paper
            sx={{
                p: compact ? 1.2 : 1.5,
                borderRadius: 2.5,
                border: `1px solid ${alpha(accent, 0.24)}`,
                background: `linear-gradient(180deg, ${tint} 0%, ${alpha('#ffffff', 0.9)} 100%)`
            }}
        >
            <Stack spacing={1}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <Box
                        sx={{
                            width: 28,
                            height: 28,
                            borderRadius: '50%',
                            display: 'grid',
                            placeItems: 'center',
                            color: accent,
                            bgcolor: alpha('#ffffff', 0.82),
                            border: `1px solid ${alpha(accent, 0.18)}`
                        }}
                    >
                        {icon}
                    </Box>
                    <Typography variant="body2" sx={{ fontWeight: 800, color: accent }}>
                        {title || (mode === 'error' ? 'Unable to load data' : 'No data available')}
                    </Typography>
                </Stack>

                {message ? (
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {message}
                    </Typography>
                ) : null}

                {actionLabel && typeof onAction === 'function' ? (
                    <Box>
                        <Button size="small" variant="outlined" onClick={onAction}>
                            {actionLabel}
                        </Button>
                    </Box>
                ) : null}
            </Stack>
        </Paper>
    );
}